class MMCartRemoveButton extends HTMLElement {
    constructor() {
      super();
  
      this.addEventListener('click', (event) => {
        event.preventDefault();
        const cartItems = this.closest('mm-cart-items') || this.closest('mm-cart-drawer-items');
        cartItems?.updateQuantity(this.dataset.index, 0, null, this);
      });
    }
}
  
customElements.define('mm-cart-remove-button', MMCartRemoveButton);

class MMCartItems extends HTMLElement {
    cartUpdateUnsubscriber = undefined;

    constructor() {
        super();

        this.init();
        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    }

    init() {
        this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

        // Progress Bar
        this.bxgyBar = document.getElementById('bxgy-progress');
        this.giftsIds = new Set();
        this.bxgyBar?.querySelectorAll('.js-promotion-item').forEach(item => {
            const productId = item.getAttribute('data-product-id');
            if (productId) this.giftsIds.add(Number(productId));
        });

        // Upsell Products
        this.upsellProducts = document.querySelectorAll('.cart-upsell-wrapper .upsell-product-item');
        const jsonText = document.querySelector('.cart-upsell-wrapper .js-upsell-products-json')?.textContent;
        this.upsellProductsJson = jsonText ? JSON.parse(jsonText) : null;

        // Featured Product
        this.featuredProduct = document.querySelector('.mm-cart-featured-product-wrapper');
        const featuredProductJson = document.querySelector('.mm-cart-featured-product-wrapper .js-featured-product-json')?.textContent;
        this.featuredProductJson = featuredProductJson ? JSON.parse(featuredProductJson) : null;

       this.initCartJSON();
       this.handleUpsellProducts();
       this.handleFeaturedProduct();
    }

    async getCartJSON() {
        const response = await fetch('/cart.js');
        if (!response.ok) {
          throw new Error('Failed to fetch cart');
        }
        return response.json();
    }

    async initCartJSON() {
        try {
            const cart = await this.getCartJSON();
            this.filterUpsellProducts(cart);
            this.filterFeaturedProduct(cart);
            this.updateProgressBar(cart);
        } catch (error) {
          console.error('Error initCartJSON():', error);
        }
    }

    updateProgressBar(cartJson) {
        const container = this.bxgyBar;
        const elements = {
            container,
            fill: container?.querySelector('#progressFill'),
            message: container?.querySelector('#bxgy-progress-message')
        };

        if (!elements.container || !elements.fill || !cartJson) return;
    
        const promotionItems = elements.container.querySelectorAll('.js-promotion-item');
        if (!promotionItems.length) return;
    
        const thresholds = Array.from(promotionItems)
            .map(item => ({
                element: item,
                threshold: this.getAttribute(item, 'data-threshold', true),
                productId: this.getAttribute(item, 'data-product-id', true),
                discountCode: this.getAttribute(item, 'data-discount-code'),
                wasReached: item.classList.contains('reached'),
                discountMessage: item.getAttribute('data-discount-message')
            }))
            .filter(t => t.threshold !== null)
            .sort((a, b) => a.threshold - b.threshold);
    
        if (!thresholds.length) return;
    
        const cartTotal = cartJson.total_price / 100;
        const maxThreshold = thresholds[thresholds.length - 1].threshold;

        const tierCount = thresholds.length;
        thresholds.forEach((tier, i) => {
            const visualPercent = ((i + 1) / tierCount) * 100;
            tier.visualPosition = visualPercent;
            tier.element.style.position = 'absolute';
            tier.element.style.left = `${visualPercent}%`;
            tier.element.style.transform = 'translateX(-50%)';
        });
        
        const state = {
            productsToAdd: [],
            productsToRemove: [],
            discountsToApply: [],
            discountsToRemove: [],
            nextTier: null,
            nextTierDiscountMessage: null
        };
    
        thresholds.forEach(tier => {
            const isReached = cartTotal >= tier.threshold;
            tier.element.classList.toggle('reached', isReached);
    
            if (isReached) {
                if (!tier.wasReached) {
                    if (tier.productId) state.productsToAdd.push(tier.productId);
                    if (tier.discountCode) state.discountsToApply.push(tier.discountCode);
                }
            } else {
                if (!state.nextTier) state.nextTier = tier;
                if (tier.wasReached) {
                    if (tier.productId) state.productsToRemove.push(tier.productId);
                    if (tier.discountCode) state.discountsToRemove.push(tier.discountCode);
                }
            }
        });

        state.discountsToApply.forEach(code => this.applyDiscountCode(code, cartJson));
        state.discountsToRemove.forEach(code => this.removeDiscountCode(code, cartJson));
        state.productsToAdd.forEach(productId => this.addProduct(productId, cartJson));
        state.productsToRemove.forEach(productId => this.removeProduct(productId, cartJson));
    
        let progressPercent;
        if (cartTotal >= maxThreshold) {
            progressPercent = 100;
        } else {
            let prevThreshold = 0;
            let prevPosition = 0;
            progressPercent = 0;
            for (let i = 0; i < thresholds.length; i++) {
                if (cartTotal < thresholds[i].threshold) {
                    const ratio = (cartTotal - prevThreshold) / (thresholds[i].threshold - prevThreshold);
                    progressPercent = prevPosition + ratio * (thresholds[i].visualPosition - prevPosition);
                    break;
                }
                prevThreshold = thresholds[i].threshold;
                prevPosition = thresholds[i].visualPosition;
            }
        }

        elements.fill.style.width = `${progressPercent}%`;
    
        if (elements.message) {
            if (cartTotal >= maxThreshold) {
                elements.message.innerHTML = "You've unlocked all discounts!";
            } else if (state.nextTier) {
                const amountLeftCents = Math.round((state.nextTier.threshold - cartTotal) * 100);
                const moneyFormat = theme.settings.moneyFormat;
                const formattedAmount = theme.Currency.formatMoney(amountLeftCents, moneyFormat);
                let msg = `You're <b>${formattedAmount}</b> away from the next reward!`;
                
                if (state.nextTier.discountMessage) msg = `You're <b>${formattedAmount}</b> away from ${state.nextTier.discountMessage}`;
                elements.message.innerHTML = msg;
            }
        }
    }

    filterUpsellProducts(cartJson) {
        if (!this.upsellProductsJson || !this.upsellProducts) return;
    
        const cartVariantIds = new Set(cartJson?.items?.map(item => item.variant_id) || []);
        this.upsellProducts.forEach((upsellEl) => {
            const productId = upsellEl.getAttribute("data-product-id");
            if (!productId) return;
    
            const upsellProduct = this.upsellProductsJson.find(p => p.id == productId);
            if (!upsellProduct) return;
    
            const existsInCart = upsellProduct.variants
            .map(v => Number(v.id))
            .some(id => cartVariantIds.has(id));
    
            upsellEl.classList.toggle("hidden", existsInCart);
        });
    }

    handleUpsellProducts() {
        if (!this.upsellProductsJson || !this.upsellProducts.length) return;

        this.upsellProducts.forEach(product => {
            const addButton = product.querySelector('.js-upsell-atc');
            if (!addButton || addButton.dataset.upsellInitialized) return;

            addButton.dataset.upsellInitialized = 'true';

            addButton.addEventListener('click', async (event) => {
                event.preventDefault();
                if (addButton.disabled) return;

                addButton.disabled = true;
                addButton.classList.add('active');
                addButton.querySelector('.cart-loader').classList.remove('hidden');

                const variantId = addButton.getAttribute('data-id');
                if (!variantId) return;

                const body = JSON.stringify({
                    id: variantId,
                    quantity: 1,
                    sections: this.getSectionsToRender().map(section => section.section)
                });

                await fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig(), ...{ body } })
                    .then((response) => response.json())
                    .then(async (parsedState) => {
                        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'upsell-cart-items', cart: parsedState });
                    })
                    .catch((error) => {
                        if (error.name === 'AbortError') {
                          console.log('Fetch aborted by user');
                        }
                        else {
                          console.error(error);
                          this.onCartError(error.message);
                        }
                      })
                    .finally(() => {
                        addButton.disabled = false;
                        addButton.classList.remove('active');
                        addButton.querySelector('.cart-loader').classList.add('hidden');
                    });
            });

        });
    }

    filterFeaturedProduct(cartJson) {
        if (!this.featuredProductJson || !this.featuredProduct) return;

        const cartVariantIds = new Set(cartJson?.items?.map(item => item.variant_id) || []);
        const featuredProductVariantId = Number(this.featuredProductJson[0].id);
        const existsInCart = cartVariantIds.has(featuredProductVariantId);
        this.featuredProduct.classList.toggle("hidden", existsInCart);
    }

    handleFeaturedProduct() {
        if (!this.featuredProductJson || !this.featuredProduct) return;

        const addButton = this.featuredProduct.querySelector('.js-featured-atc');
        if (!addButton || addButton.dataset.featuredInitialized) return;

        addButton.dataset.featuredInitialized = 'true';

        addButton.addEventListener('click', async (event) => {
            event.preventDefault();

            if (addButton.disabled) return;

            addButton.disabled = true;
            addButton.classList.add('active');
            addButton.querySelector('.cart-loader').classList.remove('hidden');

            const variantId = addButton.getAttribute('data-id');
            if (!variantId) return;

            const body = JSON.stringify({
                id: variantId,
                quantity: 1,
                sections: this.getSectionsToRender().map(section => section.section)
            });

            await fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig(), ...{ body } })
                .then((response) => response.json())
                .then(async (parsedState) => {
                    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'featured-product-cart-items', cart: parsedState });
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        console.log('Fetch aborted by user');
                    }
                    else {
                        console.error(error);
                        this.onCartError(error.message);
                    }
                })
                .finally(() => {
                    addButton.disabled = false;
                    addButton.classList.remove('active');
                    addButton.querySelector('.cart-loader').classList.add('hidden');
                });
        });
    }

    disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
            this.cartUpdateUnsubscriber();
        }
    }

    onChange(event) {
        this.validateQuantity(event);
    }

    onCartUpdate(event) {
        if (event.cart.errors) {
            this.updateLiveRegions(event.line, event.cart.errors);
            return;
        }

        const isEmpty = event.cart.item_count === 0;
        this.handleEmptyCart(isEmpty);

        if (isEmpty) {
            this.closest('mm-cart-drawer')?.classList.toggle('is-empty', isEmpty);
            this.addEmptyState();
        }

        if (!isEmpty) {
            const sectionsToRender = this.getSectionsToRender();

            sectionsToRender.forEach(section => {
                const container = document.getElementById(section.id);
                if (!container) return;

                const elementToReplace = section.selector
                    ? container.querySelector(section.selector)
                    : container;

                if (!elementToReplace) return;

                const newHTML = event.cart.sections?.[section.section];
                if (!newHTML) return;

                const parsedHTML = this.getSectionInnerHTML(newHTML, section.selector);
                if (parsedHTML) {
                    elementToReplace.innerHTML = parsedHTML;
                }
            });
        }

        (async () => {
            const cart = await this.getCartJSON();
            this.filterUpsellProducts(cart);
            this.filterFeaturedProduct(cart);
            this.updateProgressBar(cart);
        })();

        document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: {
                cart: event.cart
            }
        }));
    }

    addEmptyState() {
        const drawerInner = this.closest(".drawer__inner");
        if(!drawerInner) return;

        if (drawerInner.querySelector(".drawer__inner-empty")) {
            return;
        }

        drawerInner.insertAdjacentHTML(
            "beforeend",
            `
            <div class="drawer__inner-empty">
            <div class="cart-drawer__warnings">
                <div class="cart-drawer__empty-content">
                <h2 class="cart__empty-text">Your cart is empty</h2>
                <button class="drawer__close" type="button" onclick="this.closest('mm-cart-drawer').close()">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
                    <path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                </button>
                <a href="/collections/all" class="mm-button">Continue Shopping</a>
                </div>
            </div>
            </div>
            `
        );
    }

    handleEmptyCart(isEmpty) {
        const drawer =this.closest('mm-cart-drawer');
        if(!drawer) return;

        const isEmptyClass = drawer.classList.contains('is-empty');
        if(!isEmpty && isEmptyClass) {
            drawer.classList.remove('is-empty');
            const emptyDiv = drawer?.querySelector('.drawer__inner-empty');
            if(emptyDiv) emptyDiv.remove();
        }
    }

    updateQuantity(line, quantity, name, target) {
        this.enableLoading(line);

        const body = JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map(section => section.section)
        });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState, target, line, name });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
              this.onCartError(error.message);
            }
          })
          .finally(() => {
            this.disableLoading(line);
          });
    }

    addProduct(id, cartJson) {
        const isInCart = cartJson.items.some(
            item => item.variant_id == id && item.properties?._progressbar_promotion
        );
        if(isInCart) return;

        const body = JSON.stringify({
            id: id,
            quantity: 1,
            properties: {
                _progressbar_promotion: 'true'
            },
            sections: this.getSectionsToRender().map(section => section.section)
        });
        
        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig(), ...{ body } })
            .then((response) => response.json())
            .then((parsedState) => {
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState });
            })
            .catch((error) => {
                console.error(error);
            });
    }

    removeProduct(id, cartJson) {
        const item = cartJson.items.find(item => item.variant_id == id && item.properties?._progressbar_promotion);
        if(!item) return;

        const body = JSON.stringify({
            id: item.key,
            quantity: 0,
            sections: this.getSectionsToRender().map(section => section.section)
        });
        
        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
            .then((response) => response.json())
            .then((parsedState) => {
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState });
            })
            .catch((error) => {
                console.error(error);
            });
    }

    applyDiscountCode(code, cartJson) {
        if (cartJson.discount_codes.some(dc => dc.code === code)) return;

        const body = JSON.stringify({
            discount: code,
            sections: this.getSectionsToRender().map(section => section.section)
        });

        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } })
            .then((response) => response.json())
            .then((parsedState) => {
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
            })
            .catch((error) => {
                console.error(error);
            });
    }

    removeDiscountCode(code, cartJson) {
        const appliedCodes = cartJson.discount_codes.map(dc => dc.code);
        if (!appliedCodes.includes(code)) return;

        const remainingCodes = appliedCodes.filter(dc => dc !== code);
        const body = JSON.stringify({
            discount: remainingCodes.join(','),
            sections: this.getSectionsToRender().map(section => section.section)
        });

        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } })
            .then((response) => response.json())
            .then((parsedState) => {
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
            })
            .catch((error) => {
                console.error(error);
            });
    }

    validateQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
    }

    enableLoading(line) {
        const mainCartItems = document.getElementById('mm-main-cart-items') || document.getElementById('CartDrawer-CartItems');
        mainCartItems?.classList.add('cart__items--disabled');


        const cartItemElements = this.querySelectorAll(`#CartItem-${line} .cart-loader`);
        const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .cart-loader`);

        [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => {
        overlay.classList.remove('hidden');
        overlay.closest("mm-cart-remove-button")?.querySelector(".cart-remove-button")?.classList.add('hidden');
        });

        document.activeElement?.blur();
        this.lineItemStatusElement?.setAttribute('aria-hidden', false);
    }

    disableLoading(line) {
        const mainCartItems = document.getElementById('mm-main-cart-items') || document.getElementById('CartDrawer-CartItems');
        mainCartItems?.classList.remove('cart__items--disabled');
    
        const cartItemElements = this.querySelectorAll(`#CartItem-${line} .cart-loader`);
        const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .cart-loader`);
    
        [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => {
          overlay.classList.add('hidden');
          overlay.closest("mm-cart-remove-button")?.querySelector(".cart-remove-button")?.classList.remove('hidden');
        });
    }

    updateLiveRegions(line, message) {
        const lineItemError =
          document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
        if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;
    
        this.lineItemStatusElement?.setAttribute('aria-hidden', true);
    
        const cartStatus =
          document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
        cartStatus?.setAttribute('aria-hidden', false);
    
        setTimeout(() => {
          cartStatus?.setAttribute('aria-hidden', true);
        }, 1000);
    }

    onCartError(errors) {
        this.querySelectorAll('.cart-loader').forEach(overlay => {
          overlay.classList.add('hidden');
          overlay.closest('mm-cart-remove-button')?.querySelector('.cart-remove-button')?.classList.remove('hidden');
        });
        this.showCartError(errors);
    }

    showCartError(message) {
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if(errors) errors.textContent = message;
    }

    getSectionInnerHTML(html, selector) {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
    
    getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'mm-cart-drawer',
            selector: 'mm-cart-items'
          },
          {
            id: 'CartDrawer',
            section: 'mm-cart-drawer',
            selector: '#main-cart-drawer-footer',
          },
          {
            id: 'CartDrawer',
            section: 'mm-cart-drawer',
            selector: '.mm-drawer__header',
          },
        ];
    }

    getAttribute(el, attr, isNumber = false) {
        const val = el.getAttribute(attr);
        if (val === null || val.trim() === '') return null;
        return isNumber ? Number(val) : val;
    };
}

customElements.define('mm-cart-items', MMCartItems);


class MMQuantityInput extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.querySelectorAll('button').forEach((button) =>
            button.addEventListener('click', this.onButtonClick.bind(this))
        );
    }

    quantityUpdateUnsubscriber = undefined;

    connectedCallback() {
        this.validateQtyRules();
        this.quantityUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    }

    disconnectedCallback() {
        if (this.quantityUpdateUnsubscriber) {
            this.quantityUpdateUnsubscriber();
        }
    }

    onInputChange(event) {
        this.validateQtyRules();
    }

    onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;

        if (event.target.name === 'plus') {
            if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
                this.input.value = this.input.dataset.min;
            } else {
                this.input.stepUp();
            }
        } else {
            this.input.stepDown();
        }

        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

        if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
            this.input.value = parseInt(this.input.min);
        }
    }

    validateQtyRules() {
        const value = parseInt(this.input.value);
        if (this.input.min) {
            const buttonMinus = this.querySelector(".quantity__button[name='minus']");
            buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
        }
        if (this.input.max) {
            const max = parseInt(this.input.max);
            const buttonPlus = this.querySelector(".quantity__button[name='plus']");
            buttonPlus.classList.toggle('disabled', value >= max);
        }
    }
}

customElements.define('mm-quantity-input', MMQuantityInput);